// Runtime configuration - the single seam for all environment URLs and settings.
//
// Config is delivered at RUNTIME via `window.__APP_CONFIG`, set by a blocking
// <script src="/env.js"> in index.html BEFORE the app bundle evaluates (many
// modules read config at import time - axios.defaults.baseURL, the OIDC authority,
// isBlast - so it must be present synchronously, not fetched). The bundle is
// therefore URL-agnostic: one build runs against any environment, and the URLs are
// swapped by replacing env.js on the SWA (no rebuild).
//
// DEPLOYED: env.js carries `families` (azure + austin-<suffix> + austin-prod +
// tech-test) and the family is chosen by the origin the app was loaded from (see
// pickFamily). Every deploy ships the same family set except the env-specific azure
// family, so dev/test/prod env.js differ only in the azure hosts.
//
// LOCAL DEV: env.js additionally carries `local` = { catalog, profile }. The
// catalog is a flat map of target-name -> per-service origins (local, the AFD
// tiers dev/test/prod, the tech tiers tech-test/tech-prod, and azure env
// suffixes); the profile picks a target per service, so you can run e.g. crm
// locally while id/data hit the dev AFD. Deployed env.js has no `local` block.
//
// Every URL in env.js is a BARE ORIGIN (scheme+host, no path, no trailing slash).
// Path/format rules live here and nowhere else (getApiBase appends /api/v1, etc.),
// so callers never concatenate or trim URLs again.

export type ServiceKey =
	| "crm"
	| "id"
	| "data"
	| "mailer"
	| "portal"
	| "blast"
	| "storage";
// "azure" (this env's own hosts) plus the fixed convention families: austin-<suffix>
// (austinpowder.io), austin-prod (austinpowder.com) and tech-<suffix> (igneous.tech).
// Selection is data-driven (see pickFamily), so the set is open-ended by name.
export type Family = "azure" | `austin-${string}` | `tech-${string}`;

type ServiceUrls = Partial<Record<ServiceKey, string>>;

interface LocalConfig {
	// target-name -> per-service bare origins (e.g. "local", "dev", "tech-test",
	// "dev-wus2-v1"). Generated for local dev only.
	catalog: Record<string, ServiceUrls>;
	// service -> catalog target name. Unset services fall back to defaultTarget.
	profile: Partial<Record<ServiceKey, string>>;
	// target used for any service the profile does not name.
	defaultTarget?: string;
}

export interface AppConfig {
	env: string;
	families: Partial<Record<Family, ServiceUrls>>;
	defaultFamily: Family;
	local?: LocalConfig;

	mode: "crm" | "blast";
	isRedirect: boolean;
	sentryDsn?: string;
	clientId: string;
	responseType: string;
	appTitle: string;
	debugInfo: string;
	mockMode: boolean;
}

declare global {
	interface Window {
		__APP_CONFIG?: AppConfig;
		// Set by the index.html boot guard; called by main.tsx once the app mounts.
		__markBooted?: () => void;
		// Set by the index.html boot guard; renders the maintenance-aware error
		// page. Called by the app for a failure it catches (e.g. a lazy route
		// chunk that won't load) where the router can't show /maintenance.
		__showBootError?: () => void;
	}
}

const cfg = (): AppConfig => {
	const c = window.__APP_CONFIG;
	if (!c) {
		throw new Error(
			"window.__APP_CONFIG is missing - /env.js did not load before the app bundle.",
		);
	}
	return c;
};

// DEPLOYED family selection - data-driven, no hardcoded domain rules. The SWA is
// served from one of the family URLs (its portal/blast origin), so the family is
// simply whichever one lists a URL whose origin equals the current origin. Adding
// a family/domain to env.js is enough for it to be recognised. Falls back to
// defaultFamily when nothing matches (e.g. localhost, which uses the local block).
export const pickFamily = (
	origin: string,
	families: Partial<Record<Family, ServiceUrls>>,
	fallback: Family,
): Family => {
	for (const family of Object.keys(families) as Family[]) {
		const urls = families[family];
		if (!urls) continue;
		for (const u of Object.values(urls)) {
			try {
				if (u && new URL(u).origin === origin) return family;
			} catch {
				// ignore a malformed URL in config
			}
		}
	}
	return fallback;
};

// Resolve a service to its bare origin. Local profile/catalog wins (dev only);
// otherwise the origin-selected family, falling back to defaultFamily.
const originFor = (service: ServiceKey): string => {
	const c = cfg();

	if (c.local) {
		const target =
			c.local.profile[service] ?? c.local.defaultTarget ?? c.defaultFamily;
		const url = c.local.catalog[target]?.[service];
		if (!url) {
			throw new Error(
				`No local URL for service '${service}' at target '${target}'. Check env.js catalog/profile.`,
			);
		}
		return url;
	}

	const family = pickFamily(
		window.location.origin,
		c.families,
		c.defaultFamily,
	);
	const url =
		c.families[family]?.[service] ??
		c.families[c.defaultFamily]?.[service];
	if (!url) {
		throw new Error(
			`No URL for service '${service}' (family '${family}', env '${c.env}'). Check env.js.`,
		);
	}
	return url;
};

// ---- URL getters: the ONE place paths/formatting are applied ----------------

export const getCrmApiBase = () => `${originFor("crm")}/api/v1`;
export const getIdAuthority = () => originFor("id"); // bare origin (OIDC authority)
export const getIdApiBase = () => `${originFor("id")}/api/v1`;
export const getDataApiBase = (version: 1 | 2 = 2) =>
	`${originFor("data")}/api/v${version}`;
export const getMailerBase = () => originFor("mailer");

// Public maintenance config, read straight from Azure Blob Storage (container
// "maintenance", blob "maintenance.json"). Read directly - not via an API - so the
// maintenance page still works when the APIs themselves are down. The "storage"
// origin is the account's bare blob endpoint (https://<account>.blob.core.windows.net);
// the container/blob path is fixed and lives here, not in env.js.
export const getMaintenanceConfigUrl = () =>
	`${originFor("storage")}/maintenance/maintenance.json`;
export const getCrmUiOrigin = () => originFor("portal");
export const getBlastUiOrigin = () => originFor("blast");

// Raw origins (no path) when a caller needs the host itself.
export const getServiceOrigin = (service: ServiceKey) => originFor(service);

// The fully-resolved endpoints the app is actually using (family/profile applied,
// paths formatted). Handy to log at startup so it's obvious where a build points.
export const resolvedEndpoints = () => ({
	env: cfg().env,
	mode: cfg().mode,
	crmApi: getCrmApiBase(),
	idAuthority: getIdAuthority(),
	dataApi: getDataApiBase(2),
	mailer: getMailerBase(),
	crmUi: getCrmUiOrigin(),
	blastUi: getBlastUiOrigin(),
});

// ---- Non-URL settings -------------------------------------------------------

export const getMode = () => cfg().mode;
export const isRedirectEnabled = () => cfg().isRedirect;
export const getSentryDsn = () => cfg().sentryDsn;
export const getClientId = () => cfg().clientId;
export const getResponseType = () => cfg().responseType;
export const getAppTitle = () => cfg().appTitle;
export const getDebugInfo = () => cfg().debugInfo;
export const getMockMode = () => cfg().mockMode;
